import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { girlsAccessoriesProducts } from '../../mocks/products';
import { Link } from 'react-router-dom';
import { Button } from '../../components/base/Button';

const AccessoriesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [priceRange, setPriceRange] = useState('All');

  const categories = ['All', 'Bangles', 'Prayer Beads', 'Hijab Accessories', 'Girls\' Accessories'];
  const priceRanges = ['All', 'Under $15', '$15-$20', 'Over $20'];

  const filteredProducts = girlsAccessoriesProducts.filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory;
    
    let priceMatch = true;
    if (priceRange === 'Under $15') {
      priceMatch = product.price < 15;
    } else if (priceRange === '$15-$20') {
      priceMatch = product.price >= 15 && product.price <= 20;
    } else if (priceRange === 'Over $20') {
      priceMatch = product.price > 20;
    }
    
    return categoryMatch && priceMatch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream-100 to-white">
      <Header />
      
      {/* Hero Section - Close-up Accessory Photography */}
      <section className="relative py-24 bg-gradient-to-r from-emerald-700 to-emerald-600 overflow-hidden">
        {/* Background Image - Close-up of Bangles and Beads */}
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/a7a21d10371d3781bd03c15181f7558d.jpg"
            alt="Handcrafted Bangles & Beads"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/60 via-emerald-800/50 to-emerald-900/60"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center text-cream-100 max-w-4xl mx-auto">
            {/* Decorative divider top */}
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-16 bg-gold-500"></div>
              <div className="w-2 h-2 bg-gold-500 rounded-full mx-3"></div>
              <div className="h-px w-16 bg-gold-500"></div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white" style={{ fontFamily: '"Playfair Display", serif' }}>
              Handcrafted Bangles & Beads
            </h1>
            
            {/* Decorative divider bottom */}
            <div className="flex items-center justify-center mb-8">
              <div className="h-px w-16 bg-gold-500"></div>
              <div className="w-2 h-2 bg-gold-500 rounded-full mx-3"></div>
              <div className="h-px w-16 bg-gold-500"></div>
            </div>
            
            <p className="text-xl md:text-2xl leading-relaxed text-cream-100">
              Adorn yourself with the beauty of tradition. Each piece is carefully crafted to complement your modest elegance, from prayer beads that carry spiritual meaning to bangles that celebrate cultural heritage.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-cream-200 sticky top-20 z-40 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-semibold text-gray-700 mr-2">Category:</span>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-emerald-600 text-white shadow-md'
                      : 'bg-cream-100 text-gray-700 hover:bg-cream-200 border border-cream-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Price Filter */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-semibold text-gray-700 mr-2">Price:</span>
              {priceRanges.map(range => (
                <button
                  key={range}
                  onClick={() => setPriceRange(range)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    priceRange === range
                      ? 'bg-gold-500 text-white shadow-md'
                      : 'bg-cream-100 text-gray-700 hover:bg-cream-200 border border-cream-300'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <p className="text-gray-600 text-lg">
              Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
            {filteredProducts.map((product) => (
              <Link key={product.id} to={`/product/${product.id}`}>
                <div className="group cursor-pointer">
                  <div className="bg-cream-50 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-gold-500">
                    <div className="relative">
                      <img 
                        src={product.image}
                        alt={product.name}
                        className="w-full h-72 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 right-3">
                        <button className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-cream-100 transition-colors cursor-pointer">
                          <i className="ri-heart-line text-emerald-600 text-sm"></i>
                        </button>
                      </div>
                      {product.originalPrice > product.price && (
                        <div className="absolute top-3 left-3 bg-gold-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          Sale
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                      <div className="flex items-center mb-3">
                        <div className="flex text-gold-500 text-xs">
                          {[...Array(5)].map((_, i) => (
                            <i key={i} className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'}`}></i>
                          ))}
                        </div>
                        <span className="text-xs text-gray-500 ml-1">({product.reviews})</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-emerald-700">${product.price}</span>
                          {product.originalPrice > product.price && (
                            <span className="text-xs text-gray-500 line-through">${product.originalPrice}</span>
                          )}
                        </div>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 text-gray-300">
                <i className="ri-shopping-bag-line text-6xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No items found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your filters to see more accessories</p>
              <button
                onClick={() => {
                  setSelectedCategory('All');
                  setPriceRange('All');
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Cultural Craft Section - Flat Lay Photography */}
      <section className="py-16 bg-gradient-to-r from-cream-100 to-cream-200 relative overflow-hidden">
        {/* Background Image - Flat Lay of Accessories */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://readdy.ai/api/search-image?query=elegant%20flat%20lay%20photography%20of%20Islamic%20jewelry%20bangles%20prayer%20beads%20and%20accessories%20arranged%20artistically%20on%20cream%20silk%20fabric%20with%20gold%20accents%2C%20no%20people%2C%20overhead%20view%2C%20luxurious%20product%20styling%20with%20soft%20natural%20lighting&width=1920&height=600&seq=accessories-flatlay-1&orientation=landscape"
            alt="Handcrafted accessories"
            className="w-full h-full object-cover object-center"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="h-px w-12 bg-gold-500"></div>
              <div className="w-2 h-2 bg-gold-500 rounded-full mx-3"></div>
              <div className="h-px w-12 bg-gold-500"></div>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
              The Art of Handcrafted Beauty
            </h2>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Every bangle, every bead tells a story of tradition and craftsmanship. Our accessories are more than adornments—they're expressions of faith, culture, and timeless elegance. Handpicked and crafted with love, each piece is designed to complement your modest wardrobe with grace.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hand-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Handcrafted with Love</h3>
                <p className="text-sm text-gray-600">Each piece is carefully made by skilled artisans</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gold-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Premium Quality</h3>
                <p className="text-sm text-gray-600">Only the finest materials for lasting beauty</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Cultural Heritage</h3>
                <p className="text-sm text-gray-600">Celebrating Islamic tradition and artistry</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-700 to-emerald-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
            Looking for Something Special?
          </h2>
          <p className="text-lg text-cream-100 mb-8 max-w-2xl mx-auto">
            Can't find what you're looking for? Contact us and we'll help you find the perfect accessories to complete your modest wardrobe!
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gold-500 hover:bg-gold-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AccessoriesPage;